"use client";

import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UserCheck, UserPlus } from "lucide-react";
import "./DoctorCard.css";
import { useDoctorFollow } from "../../../ProfileDoctor/hooks/useDoctorFollow";

// ✅ Même avatar par défaut que SubMenu
const DEFAULT_AVATAR = "https://cdn-icons-png.flaticon.com/512/149/149071.png";

/** Util: pick first non-empty value */
const pick = (...vals) =>
  vals.find((v) => {
    if (v == null) return false;
    const s = String(v).trim();
    return (
      s.length > 0 &&
      s.toLowerCase() !== "undefined" &&
      s.toLowerCase() !== "null"
    );
  });

/** Concat util */
const join = (arr, sep = ", ") =>
  arr
    .filter(Boolean)
    .map((s) => String(s).trim())
    .filter(Boolean)
    .join(sep);

const mapDoctor = (doc) => {
  if (!doc || typeof doc !== "object") return null;

  const id = pick(
    doc.id,
    doc._id,
    doc.userId,
    doc.pgId,
    doc.uid,
    doc.uuid,
    doc.user?.id,
    doc.user?._id,
    doc.user?.userId
  );

  const name =
    pick(
      doc.fullName,
      doc.name,
      doc.doctorName,
      join([doc.firstName, doc.lastName], " "),
      doc.user?.fullName,
      join([doc.user?.firstName, doc.user?.lastName], " ")
    ) || "Docteur";

  const specialty =
    pick(
      doc.specialty,
      doc.speciality,
      doc.specialtyName,
      doc.user?.specialty,
      doc.user?.speciality
    ) || "";

  const image =
    pick(
      doc.avatarUrl,
      doc.avatar,
      doc.photo,
      doc.photoUrl,
      doc.imageUrl,
      doc.profilePicture,
      doc.picture,
      doc.pic,
      doc.user?.avatarUrl,
      doc.user?.avatar,
      doc.user?.photo,
      doc.user?.imageUrl
    ) || DEFAULT_AVATAR;

  const street = pick(
    doc.clinicAddress,
    doc.practicePlace,
    doc.address,
    doc.addressLine,
    doc.location?.address,
    doc.clinic?.address,
    doc.user?.address
  );
  const city = pick(
    doc.city,
    doc.location?.city,
    doc.address?.city,
    doc.clinic?.city,
    doc.user?.city
  );
  const country = pick(
    doc.country,
    doc.location?.country,
    doc.address?.country,
    doc.clinic?.country,
    doc.user?.country
  );

  const location = join([street, join([city, country], ", ")], " · ");

  return {
    id: id ? String(id) : "",
    name,
    specialty,
    image,
    location,
    nextAvailable: pick(doc.nextAvailable, doc.availability?.next) || "Soon",
  };
};

const DoctorCard = ({ doctor, index }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language?.startsWith("ar");

  useEffect(() => {
    document.documentElement.dir = isRTL ? "rtl" : "ltr";
  }, [isRTL]);

  const mappedDoctor = useMemo(() => mapDoctor(doctor), [doctor]);
  const {
    isFollowing,
    loading: followLoading,
    toggleFollow,
    canFollow,
  } = useDoctorFollow(doctor);
  if (!mappedDoctor) return null;

  const getInitials = () => {
    const { name } = mappedDoctor;
    if (!name) return "?";
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return parts[0]?.slice(0, 2).toUpperCase() || "?";
  };

  const goToDetails = () => {
    if (!mappedDoctor.id) return;
    navigate(`/doctor-details/${mappedDoctor.id}`);
  };

  const handleScheduleAppointment = (e) => {
    e.stopPropagation();
    if (!mappedDoctor.id) return;
    navigate(`/doctor-details/${mappedDoctor.id}?tab=schedule`);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      goToDetails();
    }
  };

  const disableReason = !mappedDoctor.id
    ? t("doctorCard.noId")
    : t("doctorCard.scheduleTitle");

  const ariaCard = t("doctorCard.cardAria", { name: mappedDoctor.name });
  const ariaSchedule = t("doctorCard.scheduleAria", {
    name: mappedDoctor.name,
  });

  return (
    <div
      className={`doctor-card ${isRTL ? "is-rtl" : ""}`}
      onClick={goToDetails}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={ariaCard}
      data-index={index}
    >
      <button
        type="button"
        className={`doctor-follow-button${isFollowing ? " is-following" : ""}`}
        onClick={(e) => {
          e.stopPropagation();
          toggleFollow();
        }}
        onKeyDown={(e) => e.stopPropagation()}
        disabled={followLoading || !canFollow}
        aria-label={isFollowing ? t("doctorProfileCard.actions.unfollowDoctor", "Unfollow doctor") : t("doctorProfileCard.actions.followDoctor", "Follow doctor")}
        title={isFollowing ? t("doctorProfileCard.actions.unfollowDoctor", "Unfollow doctor") : t("doctorProfileCard.actions.followDoctor", "Follow doctor")}
      >
        {isFollowing ? <UserCheck size={17} /> : <UserPlus size={17} />}
      </button>

      <div className="doctor-card-header">
        <div className="doctor-avatar-wrapper">
          <div className="doctor-avatar">
            {mappedDoctor.image && mappedDoctor.image !== DEFAULT_AVATAR ? (
              <img
                src={mappedDoctor.image}
                alt={t("doctorCard.avatarAlt", { name: mappedDoctor.name })}
                onError={(e) => {
                  e.currentTarget.src = DEFAULT_AVATAR;
                }}
              />
            ) : (
              <div className="doctor-avatar-fallback">{getInitials()}</div>
            )}
          </div>
        </div>

        <div className="doctor-info-section">
          <div className="doctor-name">Dr. {mappedDoctor.name}</div>

          {mappedDoctor.specialty && (
            <p className="doctor-specialty">{mappedDoctor.specialty}</p>
          )}

          {mappedDoctor.location && (
            <p className="doctor-location">{mappedDoctor.location}</p>
          )}

      
      
      
      
      
      
      
      
      
      
        </div>
      </div>

      <button
        className="schedule-btn"
        onClick={handleScheduleAppointment}
        disabled={!mappedDoctor.id}
        title={disableReason}
        aria-label={ariaSchedule}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          className="calendar-icon"
          aria-hidden="true"
        >
          <path
            d="M5.33301 1.33301V3.99967"
            stroke="white"
            strokeWidth="0.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M10.667 1.33301V3.99967"
            stroke="white"
            strokeWidth="0.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M14 11.3337V4.00033C14 3.6467 13.8595 3.30756 13.6095 3.05752C13.3594 2.80747 13.0203 2.66699 12.6667 2.66699H3.33333C2.97971 2.66699 2.64057 2.80747 2.39052 3.05752C2.14048 3.30756 2 3.6467 2 4.00033V13.3337C2 13.6873 2.14048 14.0264 2.39052 14.2765C2.64057 14.5265 2.97971 14.667 3.33333 14.667H10.6667L14 11.3337Z"
            stroke="white"
            strokeWidth="0.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M2 6.66699H14"
            stroke="white"
            strokeWidth="0.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M10 14.667V12.0003C10 11.6467 10.1405 11.3076 10.3905 11.0575C10.6406 10.8075 10.9797 10.667 11.3333 10.667H14"
            stroke="white"
            strokeWidth="0.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>

        {t("doctorCard.scheduleBtn")}
      </button>
    </div>
  );
};

export default DoctorCard;